import { useMemo, useState } from 'react'

const SelectedEventsPanel = ({ selectedEvents, onRemoveSelection, isMobile }) => {
  const [searchTerm, setSearchTerm] = useState('')

  const filteredEvents = useMemo(() => {
    if (!searchTerm.trim()) {
      return selectedEvents
    }
    const term = searchTerm.trim().toLowerCase()
    return selectedEvents.filter((event) => {
      const leagueName = String(event?.leagueName ?? '').toLowerCase()
      const leagueId = String(event?.leagueId ?? '').toLowerCase()
      const homeTeam = String(event?.homeTeam ?? '').toLowerCase()
      const awayTeam = String(event?.awayTeam ?? '').toLowerCase()
      const eventId = String(event?.id ?? '').toLowerCase()

      return (
        leagueName.includes(term) ||
        leagueId.includes(term) ||
        homeTeam.includes(term) ||
        awayTeam.includes(term) ||
        eventId.includes(term)
      )
    })
  }, [searchTerm, selectedEvents])

  const hasSelections = Boolean(selectedEvents.length)
  const hasMatches = Boolean(filteredEvents.length)

  return (
    <aside className="flex h-full flex-col gap-3 rounded-2xl border border-customAccent/20 bg-white/90 p-4 shadow-xl shadow-customAccent/10 backdrop-blur-sm dark:border-customMuted/30 dark:bg-customSurface/80 dark:shadow-customSurface">
      <div className='flex justify-between'>
         <header>
        <h2 className="text-lg font-semibold text-customInk dark:text-customHeading">Review selections</h2>
        <p className="text-xs text-customSecondaryText dark:text-customMuted">
          Remove events inline or adjust modifiers from the grid.
        </p>
      </header>
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-customAccent dark:text-customLightYellow">
            Selected events
          </p>
          <p className="text-lg font-medium text-customInk dark:text-customHeading">
            {selectedEvents.length} {selectedEvents.length === 1 ? 'event' : 'events'} ready to manage
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customMuted"
          htmlFor="selected-events-search"
        >
          Search selections
        </label>
        <input
          id="selected-events-search"
          type="search"
          value={searchTerm}
          placeholder="Search by league, team, or ID"
          onChange={(event) => setSearchTerm(event.target.value)}
          className="w-full rounded-xl border border-customAccent/20 bg-white px-3 py-2 text-sm text-customPrimaryText shadow-sm transition focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/30 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
        />
      </div>
      <div
        className={`${isMobile ? 'flex flex-1 flex-col gap-3' : 'flex justify-between gap-3'} overflow-y-auto rounded-xl border border-customAccent/10 bg-customSlate/60 p-3 shadow-inner dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/60`}
      >
        {!hasSelections ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-12 text-center">
            <span className="rounded-full bg-customAccent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-customAccent">
              Waiting for picks
            </span>
            <p className="max-w-[220px] text-xs text-customSecondaryText dark:text-customMuted">
              Select any event to see it appear here with quick remove controls.
            </p>
          </div>
        ) : !hasMatches ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-12 text-center">
            <span className="rounded-full bg-customAccent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-customAccent">
              No matches
            </span>
            <p className="max-w-[220px] text-xs text-customSecondaryText dark:text-customMuted">
              Adjust your search to find a league, team, or event ID in your selections.
            </p>
          </div>
        ) : (
          filteredEvents.map((event) => (
            <article
              key={event.id}
              className={`${isMobile ? 'w-full' : 'min-w-[250px]'} flex flex-col gap-3 rounded-xl border border-customAccent/20 bg-white p-3 text-sm shadow-sm transition hover:shadow-md dark:border-customSurfaceMuted dark:bg-customSurface`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-customPrimaryText dark:text-customHeading">
                    {event.homeTeam} vs {event.awayTeam}
                  </p>
                  <p className="text-xs text-customSecondaryText dark:text-customMuted">
                    {event.leagueName} - Football
                  </p>
                  <p className="text-[11px] text-customMuted dark:text-customMuted">
                    Event Time: {event?.eventTime ? new Date(event.eventTime).toLocaleString() : 'TBD'}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-wide">
                {event.single ? <span className="rounded-full bg-customAccent px-2 py-1 text-white">Single</span> : null}
                {event.parlay ? (
                  <span className="rounded-full bg-customVibrantGreen px-2 py-1 text-white">Parlay</span>
                ) : null}
                {event.single && event.fivePercent ? (
                  <span className="rounded-full bg-customAccentBlue/20 px-2 py-1 text-customAccentBlue">+5%</span>
                ) : null}
                {event.single && event.eightPercent ? (
                  <span className="rounded-full bg-customOrange/20 px-2 py-1 text-customOrange">+8%</span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => onRemoveSelection(event.id)}
                className="rounded-full border border-transparent bg-customFontRed/20 px-3 py-1 text-[11px] font-semibold text-customFontRed transition hover:border-customFontRed hover:bg-customFontRed/10 focus:outline-none focus:ring-2 focus:ring-customFontRed/30 dark:bg-customFontRed/30 dark:text-customHeading dark:hover:bg-customFontRed/20"
              >
                Remove
              </button>
            </article>
          ))
        )}
      </div>
    </aside>
  )
}

export default SelectedEventsPanel
